import { defineStore } from 'pinia'
import axios, { AxiosError } from 'axios'
import { useCacheService } from '@/shared/composables/useCacheService'
import { formatDate } from '@/shared/utils'
import { useApiNew } from '@/shared/composables/useApi'
import { PolicyListItem } from '../types/policy.types'

interface PolicyState {
  person: {
    passportNo?: string
    birthdate?: Date
  }
  policyList: PolicyListItem[]
  tempPolicyItem?: PolicyFile
}

interface PolicyFile {
  policyNo: string
  url: string
}

const POLICY_STATE_KEY = 'policy-state'
const apiService = useApiNew()
const cacheService = useCacheService()

export const ERROR_TOO_MANY_REQUETS = 'TOO MANY REQUESTS ERROR'
const ERROR_NOT_FOUND = 'NOT FOUND ERROR'

const isToManyRequestsError = (error: Error | AxiosError): boolean => axios.isAxiosError(error) && error?.response?.status === 429

export const usePolicyStore = defineStore('policy', {
  state: (): PolicyState => {
    const cached = cacheService.get(POLICY_STATE_KEY)
    const passportNo = cached?.passportNo
    const birthdate = cached?.birthdate ? new Date(cached.birthdate) : undefined

    return {
      person: {
        passportNo,
        birthdate,
      },
      policyList: [],
    }
  },
  actions: {
    async fetchPolicyList(
      passportNo: string,
      birthdate: Date,
      captchaToken: string,
      controller?: AbortController
    ): Promise<PolicyListItem[] | undefined> {
      if (this.policyList.length) {
        return Promise.resolve(this.policyList)
      }

      try {
        const resp = await apiService.policyService?.getPolicyList(
          passportNo,
          formatDate(birthdate, 'en'),
          captchaToken,
          controller
        )
        this.policyList = resp || []
        return this.policyList
      } catch (error: any) {
        if (isToManyRequestsError(error)) {
          throw new Error(ERROR_TOO_MANY_REQUETS)
        }

        if (error.message === 'no_name_error') {
          throw new Error(ERROR_NOT_FOUND)
        }

        throw error
      }
    },
    async fetchPolicyDocument(
      passportNo: string,
      policyNo: string,
      controller?: AbortController
    ): Promise<string> {
      if (this.tempPolicyItem && this.tempPolicyItem.policyNo === policyNo) {
        return Promise.resolve(this.tempPolicyItem.url)
      }
      try {
        const resp = await apiService.policyService?.getPolicyDocument(
          passportNo,
          policyNo,
          controller
        )
        if (resp?.data && typeof resp.data === 'string' && !resp.data.includes('<html')) {
          const responseURL = resp.request.responseURL
          this.setPolicyFile(policyNo, responseURL)
          return responseURL
        }
        throw new Error(ERROR_NOT_FOUND)
      } catch (error) {
        if (isToManyRequestsError(error as AxiosError)) {
          throw new Error(ERROR_TOO_MANY_REQUETS)
        }

        throw error
      }
    },
    async fetchPolicyDocumentByLink(link: string) {
      try {
        const resp = await apiService.policyService?.getPolicyDocumentByLink(link)
        if (resp?.data && typeof resp.data === 'string' && !resp.data.includes('<html')) {
          return resp.request.responseURL
        }

        throw new Error(ERROR_NOT_FOUND)
      } catch (error) {
        console.warn(error)
      }
    },
    setPerson(passportNo: string, birthdate?: Date) {
      this.person.passportNo = passportNo
      this.person.birthdate = birthdate || this.person.birthdate
      cacheService.set(POLICY_STATE_KEY, this.person)
    },
    setPolicyFile(policyNo: string, url: string): void {
      this.tempPolicyItem = {
        policyNo,
        url,
      }
    },
    clearPolicyFile() {
      this.tempPolicyItem = undefined
    },
    clearPolicyList() {
      this.policyList = []
    },
  },
})
